import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Button,
  Checkbox,
  CheckboxProps,
  Image,
  Input,
  message,
  Popconfirm,
  Radio,
  Table,
  TableColumnsType,
  TableProps,
  Typography,
} from "antd";
import { IconActionKey, IconArrowDown, IconDownload2 } from "assets/svg";
import { EXHIBIT_FILTER_SORT } from "common/constants";
import { Product, ProductCompanyFilter } from "common/interfaces";
import PaginateListMobile from "components/PaginateListMobile";
import { Link, useLocation, useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import {
  useDeleteProductCompanyMutation,
  useDownloadProductCompanyCsvMutation,
  useGetProductCompanyQuery,
} from "reducers/productApi";
import { formatProductQuantity, parseErrorMessage } from "common/utils";
import CustomBreadCrumb from "components/CustomBreadCrumb";
import { usePagination } from "common/hooks/usePagination";
import ProductCompanyItem from "components/ProductCompanyItem";
import { FILE_BASE_URL } from "config/apiConfig";
import { formatPrice } from "common/helper/price.helper";
import { UTCToJapanTime } from "common/helper/time.helper";
import { useCommonContext } from "contexts/CommonContext";
import { PAGINATION } from "common/enums/Pagination.enum";
import {
  PRODUCT_FILTER_STATUS,
  ProductStatusMap,
} from "common/enums/Product.enum";

const columns: TableColumnsType<Product> = [
  {
    title: "出品ID",
    dataIndex: "id",
    align: "center",
    render: (value: number, record: Product) => (
      <Link
        to={`/admin/company-management/${record.company_id}/product/${record.code}/view`}
        className="text-primary"
      >
        {record.code}
      </Link>
    ),
  },
  {
    title: "商品名",
    dataIndex: "product_name",
    align: "center",
    render: (value: number, record: Product) => (
      <div className="flex items-center gap-[10px] self-stretch">
        <Image
          src={`${FILE_BASE_URL}/${record.image_url}`}
          className="!w-[40px] !h-[40px] object-cover"
        />
        <Typography>{value}</Typography>
      </div>
    ),
  },
  {
    title: "数量",
    dataIndex: "quantity_value",
    align: "center",
    render: (_value: number, record: Product) => formatProductQuantity(record),
  },
  {
    title: "販売価格",
    dataIndex: "price",
    align: "center",
    render: (value) => formatPrice(value),
  },
  {
    title: "出品日",
    dataIndex: "created_at",
    align: "center",
    render: (value) => {
      const timeJapan = UTCToJapanTime(value);
      return `${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`;
    },
  },
  {
    title: "ステータス",
    dataIndex: "status",
    align: "center",
    render: (value) => ProductStatusMap[value],
  },
  {
    title: "操作",
    dataIndex: "id",
    align: "center",
    render: (value: number, record: Product) => (
      <>
        <Link
          to={`/admin/company-management/${record.company_id}/product/${record.code}/view`}
          className="text-primary"
        >
          詳細
        </Link>
      </>
    ),
  },
];

const CompanyProductsPage = () => {
  const { setHeaderTitle } = useCommonContext();
  const { companyId } = useParams();
  const location = useLocation();
  const companyCode = location.state?.companyCode;
  const [showFilter, setShowFilter] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRowKeyMobile, setSelectedRowKeyMobile] = useState<string[]>(
    [],
  );
  const [searchValueDebounced, setSearchValueDebounced] = useState<
    string | undefined
  >("");
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const {
    pagination,
    paginationMobile,
    onChangePage,
    reset: resetPagination,
    resetMobile: resetPaginationMobile,
    updateTotal,
    updateTotalMobile,
  } = usePagination();

  const defaultValues: ProductCompanyFilter = {
    search: "",
    status: [
      PRODUCT_FILTER_STATUS.AVAILABLE.value,
      PRODUCT_FILTER_STATUS.DRAFT.value,
      PRODUCT_FILTER_STATUS.SUSPENDED.value,
      PRODUCT_FILTER_STATUS.SOLD.value,
      PRODUCT_FILTER_STATUS.CANCEL.value,
      PRODUCT_FILTER_STATUS.DELETE.value,
      PRODUCT_FILTER_STATUS.PRIVATE.value,
    ],
    sort: EXHIBIT_FILTER_SORT.LIST_DATE.value,
    company_id: companyId,
  };

  const { handleSubmit, control, watch, reset } = useForm<ProductCompanyFilter>(
    {
      defaultValues,
    },
  );

  const productCompanyList: ProductCompanyFilter = watch();
  const prevCompanyList = useRef<ProductCompanyFilter>(defaultValues);

  const {
    data: productCompanyData,
    error: productCompanyErr,
    refetch: refetchProductCompany,
  } = useGetProductCompanyQuery({
    filter: {
      ...productCompanyList,
      search: searchValueDebounced,
    },
    page: pagination.current,
  });

  const {
    data: productCompanyMobileData,
    error: productCompanyMobileErr,
    refetch: refetchProductCompanyMobile,
  } = useGetProductCompanyQuery({
    filter: {
      ...productCompanyList,
      search: searchValueDebounced,
    },
    page: paginationMobile.current,
  });

  const [deleteProductCompany] = useDeleteProductCompanyMutation();
  const [downloadProductCompanyCsv] = useDownloadProductCompanyCsvMutation();

  const plainStatusOptions = Object.values(PRODUCT_FILTER_STATUS).map(
    (o) => o.value,
  );

  const [checkedStatusList, setCheckedStatusList] =
    useState<string[]>(plainStatusOptions);

  const checkAllStatus = plainStatusOptions.length === checkedStatusList.length;
  const indeterminateStatus =
    checkedStatusList.length > 0 &&
    checkedStatusList.length < plainStatusOptions.length;

  const toggleFilter = () => setShowFilter((prev) => !prev);

  const rowSelection: TableProps<Product>["rowSelection"] = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({ name: record.code }),
    hideSelectAll: true,
  };

  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      const allKeys =
        productCompanyData?.data?.items?.map((item) => item.code) ?? [];
      setSelectedRowKeys(allKeys);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleCheckAllMobile = (checked: boolean) => {
    if (checked) {
      setSelectedRowKeyMobile(
        productCompanyMobileData?.data?.items.map((item) => item.code) || [],
      );
    } else {
      setSelectedRowKeyMobile([]);
    }
  };

  const downloadCsv = async () => {
    await downloadProductCompanyCsv({
      filter: productCompanyList,
      fileName: "ユーザー管理.csv",
    });
  };

  const confirm = async () => {
    try {
      const keys = !isEmpty(selectedRowKeys)
        ? selectedRowKeys
        : !isEmpty(selectedRowKeyMobile)
          ? selectedRowKeyMobile
          : [];
      if (!isEmpty(keys)) {
        await deleteProductCompany(keys).unwrap();
        message.success("削除に成功しました");
        resetPagination();
        resetPaginationMobile();
      }
    } catch (err) {
      message.error(parseErrorMessage(err));
    }
  };

  const onReset = async () => {
    resetPagination();
    resetPaginationMobile();
    reset();
    setSearchValueDebounced("");
    setCheckedStatusList(plainStatusOptions);
  };

  const onSubmit = async () => {
    resetPagination();
    resetPaginationMobile();
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    setSearchValueDebounced(productCompanyList.search);
    setTimeout(() => {
      refetchProductCompany();
      refetchProductCompanyMobile();
    }, 0);
  };

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearchValueDebounced(productCompanyList.search);
    }, 2000);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [productCompanyList.search]);

  useEffect(() => {
    if (
      JSON.stringify(prevCompanyList.current) !==
      JSON.stringify(productCompanyList)
    ) {
      resetPagination();
      resetPaginationMobile();
      prevCompanyList.current = productCompanyList;
    }
  }, [productCompanyList]);

  useEffect(() => {
    if (pagination.current === PAGINATION.PAGE) {
      refetchProductCompany();
    }
  }, [pagination.current]);

  useEffect(() => {
    if (paginationMobile.current === PAGINATION.PAGE) {
      refetchProductCompanyMobile();
    }
  }, [paginationMobile.current]);

  useEffect(() => {
    if (productCompanyData?.data?.meta?.total_items) {
      updateTotal(productCompanyData.data.meta.total_items);
    }
  }, [productCompanyData]);

  useEffect(() => {
    if (productCompanyMobileData?.data?.meta?.total_items) {
      updateTotalMobile(productCompanyMobileData.data.meta.total_items);
    }
  }, [productCompanyMobileData]);

  useEffect(() => {
    if (productCompanyErr)
      void message.error(parseErrorMessage(productCompanyErr));
  }, [productCompanyErr]);

  useEffect(() => {
    if (productCompanyMobileErr)
      void message.error(parseErrorMessage(productCompanyMobileErr));
  }, [productCompanyMobileErr]);

  useEffect(() => {
    setHeaderTitle("出品一覧");
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full product-list">
      <div className="flex min-h-[752px] px-5 pt-5 md:p-[40px] pb-[100px] flex-col items-center gap-[30px] self-stretch">
        <div className="flex flex-col items-start gap-[10px] md:gap-[20px] self-stretch">
          <CustomBreadCrumb
            items={[
              {
                label: "ユーザー管理",
                link: "/admin/company-management",
              },
              {
                label: `ユーザー詳細（ID: #${companyCode}）`,
                link: `/admin/company-management/${companyCode}/view`,
              },
              {
                label: "出品一覧",
                isCurrent: true,
              },
            ]}
          />
          <Controller
            name="search"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                prefix={<IconActionKey />}
                placeholder="出品ID、商品名などで検索"
                className="w-full h-[60px] outline outline-[3px] outline-offset-[-3px] outline-primary"
              />
            )}
          />
          <div
            data-プロパティ1="base"
            className="inline-flex justify-start items-center cursor-pointer"
            onClick={toggleFilter}
          >
            <div className="justify-start text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
              検索条件を追加
            </div>
            <IconArrowDown className="w-[20px] h-[20px] aspect-[1/1]" />
          </div>
          {showFilter && (
            <div className="self-stretch p-[20px] md:p-10 bg-primary-light rounded-xl inline-flex flex-col justify-center items-center md:gap-7">
              <div className="self-stretch justify-start text-primary text-[18px] md:text-xl font-bold font-['Noto_Sans_JP'] leading-loose">
                検索条件（フィルター）
              </div>
              <div className="self-stretch flex flex-col justify-start items-start">
                <div className="self-stretch py-5 border-b border-white inline-flex flex-col md:flex-row justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    ステータス
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start">
                    <Controller
                      control={control}
                      name="status"
                      defaultValue={plainStatusOptions}
                      render={({ field }) => {
                        const onChange = (list: string[]) => {
                          setCheckedStatusList(list);
                          field.onChange(list);
                        };
                        const onCheckAllChange: CheckboxProps["onChange"] = (
                          e,
                        ) => {
                          const newList = e.target.checked
                            ? plainStatusOptions
                            : [];
                          setCheckedStatusList(newList);
                          field.onChange(newList);
                        };
                        return (
                          <>
                            <Checkbox
                              indeterminate={indeterminateStatus}
                              onChange={onCheckAllChange}
                              checked={checkAllStatus}
                            >
                              全て
                            </Checkbox>
                            <Checkbox.Group
                              {...field}
                              options={Object.values(PRODUCT_FILTER_STATUS)}
                              value={checkedStatusList}
                              onChange={onChange}
                            />
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="self-stretch py-5 flex-col md:flex-row inline-flex justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    並び替え
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start">
                    <Controller
                      control={control}
                      name="sort"
                      render={({ field }) => (
                        <Radio.Group
                          {...field}
                          options={Object.values(EXHIBIT_FILTER_SORT)}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="w-full md:w-auto flex-col-reverse md:flex-row inline-flex justify-start items-start gap-[10px] md:gap-5">
                <Button
                  block
                  type="primary"
                  htmlType="reset"
                  onClick={onReset}
                  className="min-w-full md:min-w-[340px] min-h-14 bg-white outline outline-2 outline-offset-[-2px] outline-primary text-primary text-lg font-bold"
                >
                  クリア
                </Button>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  className="min-w-full md:min-w-[340px] min-h-14 bg-primary text-lg font-bold"
                >
                  検索する
                </Button>
              </div>
            </div>
          )}
          <div className="self-stretch flex-col-reverse md:flex-row inline-flex justify-between items-center gap-3.5">
            <div className="hidden self-stretch md:inline-flex justify-start items-center gap-2.5">
              <Checkbox
                checked={
                  !!productCompanyData?.data?.items &&
                  selectedRowKeys.length ===
                    productCompanyData.data.items.length &&
                  productCompanyData.data.items.length > 0
                }
                indeterminate={
                  !!productCompanyData?.data?.items &&
                  selectedRowKeys.length > 0 &&
                  selectedRowKeys.length < productCompanyData.data.items.length
                }
                onChange={(e) => handleCheckAll(e.target.checked)}
              >
                全てにチェックを入れる
              </Checkbox>
            </div>
            <div className="md:hidden self-stretch inline-flex justify-start items-center gap-2.5">
              <Checkbox
                checked={
                  !!productCompanyMobileData?.data?.items &&
                  selectedRowKeyMobile.length ===
                    productCompanyMobileData.data.items.length &&
                  productCompanyMobileData.data.items.length > 0
                }
                indeterminate={
                  !!productCompanyMobileData?.data?.items &&
                  selectedRowKeyMobile.length > 0 &&
                  selectedRowKeyMobile.length <
                    productCompanyMobileData.data.items.length
                }
                onChange={(e) => handleCheckAllMobile(e.target.checked)}
              >
                全てにチェックを入れる
              </Checkbox>
            </div>
            <div className="flex flex-col-reverse md:flex-row gap-3.5">
              <Popconfirm
                title="タスクを削除する"
                description="このタスクを削除してもよろしいですか?"
                onConfirm={confirm}
                okText="はい"
                cancelText="いいえ"
                disabled={
                  isEmpty(selectedRowKeys) && isEmpty(selectedRowKeyMobile)
                }
              >
                <div
                  data-プロパティ1="text only"
                  className="flex justify-start items-center cursor-pointer"
                >
                  <div className="justify-start text-blue-900 text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
                    <>チェックした項目を削除</>
                  </div>
                </div>
              </Popconfirm>
              <Button
                type="primary"
                className="h-10 min-w-24 bg-primary"
                icon={
                  <IconDownload2 className="w-[20px] h-[20px] aspect-[1/1]" />
                }
                onClick={downloadCsv}
              >
                CSVデータをダウンロード
              </Button>
            </div>
          </div>
          <Table<Product>
            className="w-full hidden md:block"
            rowKey="code"
            rowSelection={rowSelection}
            onChange={onChangePage}
            pagination={
              !isEmpty(productCompanyData?.data?.items) ? pagination : false
            }
            columns={columns}
            dataSource={productCompanyData?.data?.items || []}
          />
          <PaginateListMobile<Product>
            selectedRowKeyMobile={selectedRowKeyMobile}
            setSelectedRowKeyMobile={setSelectedRowKeyMobile}
            pagination={
              !isEmpty(productCompanyData?.data?.items)
                ? paginationMobile
                : false
            }
            dataSource={productCompanyMobileData?.data?.items || []}
            renderItem={(item, checked, onSelect) => (
              <ProductCompanyItem<Product>
                product={item}
                checked={checked}
                onSelect={onSelect}
              />
            )}
          />
        </div>
      </div>
    </form>
  );
};

export default CompanyProductsPage;
